import {makeAutoObservable} from 'mobx';

import {configureTrace} from '@yourcoach/trace';

import {not} from './conditionalUtils';

type Env = {
  // use to read unread events from opposite env (core/b2b)
  apiEndpoint?: string;
  apiVersion?: string;
  appEnv: 'dev' | 'prod' | 'rc' | 'stage1' | 'stage2' | 'test';
  b2bApiEndpoint?: string;
  b2bApiVersion?: string;
  communityProgramId: string;
  googleMapsKey: string;
  isB2b?: boolean;
  isB2bWidget?: boolean;
  isDtc?: boolean;
  stripeKey: string;
  /** Mobile only: comma-separated trace channels from `.env`. */
  traceChannels?: string;
  vonageAppId: string;
  webAppUrl?: string;
};

class EnvManager {
  private env: Env | null = null;

  constructor() {
    if (process.env.NODE_ENV === 'test') {
      this.env = {
        appEnv: 'test',
        communityProgramId: 'community_program_id',
        googleMapsKey: 'google_maps_key',
        // can be override in tests
        isB2b: false,
        stripeKey: 'stripe_key',
        vonageAppId: 'vonage_app_id',
      };
    } else {
      makeAutoObservable(this, undefined, {
        autoBind: true,
      });
    }
  }

  get isB2b() {
    return Boolean(this.env?.isB2b);
  }

  get isB2bWidget() {
    return Boolean(this.env?.isB2bWidget);
  }

  get isCore() {
    return not(this.isB2b);
  }

  get isDtc() {
    return Boolean(this.env?.isDtc);
  }

  get isProd() {
    return Boolean(this.env?.appEnv === 'prod');
  }

  get isReady() {
    return Boolean(this.env);
  }

  __setEnv(env: Env) {
    this.env = env;
    configureTrace({appEnv: env.appEnv, channels: env.traceChannels});
  }

  get<T extends keyof Env>(key: T) {
    if (!this.env) {
      throw new Error(
        'EnvManager: "env" is not provided! Please, call "envManager.__setEnv"',
      );
    }

    return this.env[key] as Env[T];
  }
}

const envManager = new EnvManager();

export {envManager};
